import moment from "moment";
import Resizer from 'react-image-file-resizer';
import {closeDialog} from "../slices/dialog";
import {resetSelected} from "../slices/selected";
import {resetProfile} from "../slices/profile";
import {resetAuthstate} from "../slices/auth";
import {closeAlert} from "../slices/alert";
import {softSkillsDataset} from "../db/models/const";
import {StudentCvInterface} from "../db/models/student/cv";
import {
    CompanyRmI,
    RmBgItemWithImportanceI,
    RmCompetenceI,
    RmJobI,
    RmLanguageI,
    RmMaterialI,
    RmQualificationI
} from "../db/models/company/rm";
import {StudentInterface} from "../db/models/student/student";
import {DocTimestamp} from "../db/models/extra";
import {CompanyGameInterface} from "../db/models/company/game";

export const getAgeFromBirthday = birthday => {
    if (!birthday) return null;
    const now = moment();
    const born = moment(birthday.seconds * 1000);
    const age = moment.duration(now.diff(born));

    return age.years();
}

export const scrollToTop = (ref) => {
    ref.current.scrollIntoView();
}

export const isSteps = companyId => {
    return companyId === "fFegWdWWkPOBWbN2FhVWmrSwXlH3";
}

export const fileRequestToExtensions = request => {
    if (!request) return "";

    if (request === "pdf") return ".pdf";
    else if (request === "image") return ".png, .jpg, .jpeg, .tif, .tiff, .svg";
    else if (request === "audio") return ".aac, .ogg, .mp3, .mpeg, .opus";
    else if (request === "video") return ".mp4";
    else if (request === "excel") return ".csv, .xlsx, .xls";
    else return "";
}

export const hasQualification = (cv: StudentCvInterface, rmQualification: RmQualificationI) => {
    if (!cv || !rmQualification) return 0;
    const rmQualificationScopeIds = rmQualification.scopeIds || [];
    const optional = rmQualification?.importance === 2 ? true : false;
    const cvQualifications = Object.values(cv.bg?.qualifications || {});

    for (const rmScopeId of rmQualificationScopeIds) {

        const found = cvQualifications.filter((cvQual) => {
            // cvQual: user qualification
            return cvQual.scopeId === rmScopeId
        }).sort((a, b) => b.typeId - a.typeId)?.[0];

        if (!found) continue;
        else if (found.typeId < rmQualification.typeId) {
            continue;
        } else {
            if (found.typeId === rmQualification.typeId) {
                if (found?.end) return 2;
                else return 1;
            } else {
                return 2;
            }
        }
    }

    if (optional) return -1;
    return 0;
};

export const hasJob = (cv: StudentCvInterface, rmJob: RmJobI) => {
    if (!cv || !rmJob) return 0;
    const optional = rmJob?.importance === 2 ? true : false;
    const cvJobs = Object.values(cv?.bg?.jobs || {});
    const rmScopeIds = rmJob?.scopeIds ? rmJob.scopeIds :
        rmJob?.scopeId ? [rmJob.scopeId] : [];

    let totalMonths = 0;
    for (const cvJob of cvJobs) {
        if (rmScopeIds.includes(cvJob.scopeId)) {
            if (rmJob?.contractId != null && rmJob.contractId > cvJob.contractId)
                continue;
            const begin = cvJob.begin ? moment((cvJob.begin as DocTimestamp).seconds * 1000) : moment();
            const end = cvJob.end ? moment((cvJob.end as DocTimestamp).seconds * 1000) : moment();
            const diff = Math.round(end.diff(begin, "months", true));
            totalMonths += diff;
        }
    }
    const requiredMonths = (rmJob?.years || 0) * 12 + (rmJob?.months || 0);


    if (totalMonths === 0) {
        if (optional) return -1;
        else return 0;
    } else if (totalMonths < requiredMonths) return 1;
    else return 2;
};

export const hasLanguage = (cv: StudentCvInterface, rmLanguage: RmLanguageI) => {
    if (!cv || !rmLanguage) return 0;
    const optional = rmLanguage?.importance === 2 ? true : false;
    const cvLanguages = Object.values(cv.bg?.languages || {});

    const cvLanguage = cvLanguages.find((x) => x.labelId === rmLanguage.labelId);
    if (!cvLanguage) {
        if (optional) return -1;
        else return 0;
    } else if (cvLanguage.levelId < rmLanguage.levelId) {
        if (optional) return -1;
        else return 1;
    } else {
        return 2;
    }
};

export const hasCompetence = (cv: StudentCvInterface, rmCompetence: RmCompetenceI) => {
    if (!cv || !rmCompetence) return 0;
    const optional = rmCompetence?.importance === 2 ? true : false;
    const cvCompetences = Object.values(cv.bg?.competences || {});

    const cvCompetence = cvCompetences.find(
        (x) => x.labelId === rmCompetence.labelId
    );
    if (!cvCompetence) {
        if (optional) return -1;
        else return 0;
    } else if (cvCompetence.levelId < rmCompetence.levelId) {
        if (optional) return -1;
        else return 1;
    } else {
        return 2;
    }
};

export const sortBeginEndItems = (list) => {
    if (!list) return [];

    return list.sort((a, b) => {
        // check if "begin" field is missing
        if (a.begin && !b.begin) return -1;
        if (!a.begin && b.begin) return 1;
        if (!a.begin && !b.begin) return 1;

        //compare "begin" values
        const secA = a.begin.seconds;
        const secB = b.begin.seconds;

        if (secA !== secB) {
            return secB - secA;
        } else {
            // same begin => use end values
            if (a.end && b.end) {
                // compare end values
                return b.end.seconds - a.end.seconds;
            }
            // some end values missing
            if (!a.end || !b.end) {
                return a.end ? -1 : 1;
            }
        }
        return 1;
    });
};

export const sortDateItems = (list) => {
    if (!list) return [];

    return list.sort((a, b) => {
        // check if "date" field is missing
        if (a.date && !b.date) return -1;
        if (!a.date && b.date) return 1;
        if (!a.date && !b.date) return 1;

        //compare "date" values
        const secA = a.date.seconds;
        const secB = b.date.seconds;

        return secB - secA;
    });
};

export const sortCvBg = (bg) => {
    if (!bg) return {};

    const qualifications = sortBeginEndItems(Object.values(bg.qualifications || {}));
    const jobs = sortBeginEndItems(Object.values(bg.jobs || {}));
    const competences = sortDateItems(Object.values(bg.competences || {}));
    const languages = sortDateItems(Object.values(bg.languages || {}));
    const others = sortBeginEndItems(Object.values(bg.others || {}));

    return {...bg, qualifications, jobs, competences, languages, others};
};

export const affinityPercentage = (cv: StudentCvInterface, rm: CompanyRmI) => {
    let total = 0;
    let partial = 0;

    // if importance === 1 => item is mandatory
    // if importance === 2 => item is optional

    const languages = Object.values(rm?.bg?.languages || {});
    const hasMandatoryLanguages = languages.filter(x => !(x?.importance === 2)).map(l => hasLanguage(cv, l));
    total += hasMandatoryLanguages.length * 2;
    partial += hasMandatoryLanguages.reduce((a: number, b: number) => a + b, 0);

    const competences = Object.values(rm?.bg?.competences || {});
    const hasMandatoryCompetences = competences.filter(x => !(x?.importance === 2)).map(c => hasCompetence(cv, c));
    total += hasMandatoryCompetences.length * 2;
    partial += hasMandatoryCompetences.reduce((a: number, b: number) => a + b, 0);

    const hasMaterials = Object.values(rm?.bg?.materials || {}).map(m => hasMaterial(cv, m));
    total += hasMaterials.length * 2;
    partial += hasMaterials.reduce((a: number, b: number) => a + b, 0);

    return Math.round(total === 0 ? 0 : partial / total * 100);
}

export const getOptionalRatio = (cv: StudentCvInterface, rm: CompanyRmI) => {
    let partial = 0;
    let total = 0;
    const rmQualifications = Object.values(rm?.bg?.qualifications || {});
    const rmJobs = Object.values(rm?.bg?.jobs || {});
    const rmLanguages = Object.values(rm?.bg?.languages || {});
    const rmCompetences = Object.values(rm?.bg?.competences || {});

    rmQualifications.filter(x => x?.importance === 2).forEach(q => {
        total++;
        if (hasQualification(cv, q) === 2) partial++;
    });
    rmJobs.filter(x => x?.importance === 2).forEach(j => {
        total++;
        if (hasJob(cv, j) === 2) partial++;
    });
    rmLanguages.filter(x => x?.importance === 2).forEach(l => {
        total++;
        if (hasLanguage(cv, l) === 2) partial++;
    });
    rmCompetences.filter(x => x?.importance === 2).forEach(c => {
        total++;
        if (hasCompetence(cv, c) === 2) partial++;
    });

    return partial.toString() + "/" + total.toString();
}

export const resizeFile = file => new Promise(resolve => {
    Resizer.imageFileResizer(file, 480, 480, 'JPEG', 80, 0, uri => {
        resolve(uri);
    }, 'blob', 300, 300);
});

export const getTimeChartRange = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    let start, end;

    if (month >= 0 && month < 3) {
        start = new Date(year, 0, 1);
        end = new Date(year, 2, 31);
    }
    if (month >= 3 && month < 6) {
        start = new Date(year, 3, 1);
        end = new Date(year, 5, 30);
    }
    if (month >= 6 && month < 9) {
        start = new Date(year, 6, 1);
        end = new Date(year, 8, 30);
    }
    if (month >= 9 && month < 12) {
        start = new Date(year, 9, 1);
        end = new Date(year, 11, 31);
    }

    return {start, end};
}

export const resetStore = dispatch => {
    dispatch(resetAuthstate());
    dispatch(resetSelected());
    dispatch(closeDialog());
    dispatch(closeAlert());
    dispatch(resetProfile());
}

export const getDoneTotalNotOptionalQualifications = (rm: CompanyRmI, cv: StudentCvInterface) => {
    if (!rm) return {
        done: 0,
        total: 0
    };

    let done = 0;
    let total = 0;

    Object.values(rm.bg?.qualifications || {}).filter(x => !(x?.importance === 2)).forEach(q => {
        total++;
        if (hasQualification(cv, q) === 2) done++;
    });

    return {done, total};
}

export const getDoneTotalOptionalQualifications = (rm: CompanyRmI, cv: StudentCvInterface) => {
    if (!rm) return {done: 0, total: 0};

    let done = 0;
    let total = 0;

    Object.values(rm.bg?.qualifications || {}).filter(x => (x?.importance === 2)).forEach(q => {
        total++;
        if (hasQualification(cv, q) === 2) {
            done++;
        }
    });

    return {done, total};
}

export const getDoneTotalNotOptionalJobs = (rm: CompanyRmI, cv: StudentCvInterface) => {
    if (!rm) return {
        done: 0,
        total: 0
    };

    let done = 0;
    let total = 0;

    Object.values(rm.bg?.jobs || {}).filter(x => !(x?.importance === 2)).forEach(j => {
        total++;
        if (hasJob(cv, j) === 2) done++;
    });

    return {done, total};
}

export const getDoneTotalOptionalJobs = (rm: CompanyRmI, cv: StudentCvInterface) => {
    if (!rm) return {done: 0, total: 0};

    let done = 0;
    let total = 0;

    Object.values(rm.bg?.jobs || {}).filter(x => (x?.importance === 2)).forEach(j => {
        total++;
        if (hasJob(cv, j) === 2) done++;
    });

    return {done, total};
}

export const getDoneTotalNotOptionalLanguages = (rm: CompanyRmI, cv: StudentCvInterface) => {
    if (!rm) return {done: 0, total: 0};

    let done = 0;
    let total = 0;

    Object.values(rm.bg?.languages || {}).filter(x => !(x?.importance === 2)).forEach(l => {
        total++;
        if (hasLanguage(cv, l) === 2) done++;
    });

    return {done, total};
}

export const getDoneTotalOptionalLanguages = (rm: CompanyRmI, cv: StudentCvInterface) => {
    if (!rm) return {done: 0, total: 0};

    let done = 0;
    let total = 0;

    Object.values(rm.bg?.languages || {}).filter(x => (x?.importance === 2)).forEach(l => {
        total++;
        if (hasLanguage(cv, l) === 2) done++;
    });

    return {done, total};
}

export const getDoneTotalOptionalSkills = (rm: CompanyRmI, cv: StudentCvInterface) => {
    if (!rm || !cv) return {};

    const language = getDoneTotalOptionalLanguages(rm, cv);
    const competence = getDoneTotalOptionalCompetences(rm, cv);
    const material = getDoneTotalOptionalMaterials(rm, cv);

    return {
        done: language.done + competence.done + material.done,
        total: language.total + competence.total + material.total
    };
}

export const getDoneTotalMandatorySkills = (rm: CompanyRmI, cv: StudentCvInterface) => {
    if (!rm || !cv) return {};

    const language = getDoneTotalNotOptionalLanguages(rm, cv);
    const competence = getDoneTotalNotOptionalCompetences(rm, cv);
    const material = getDoneTotalNotOptionalMaterials(rm, cv);

    return {
        done: language.done + competence.done + material.done,
        total: language.total + competence.total + material.total
    };
}

export const getDoneTotalMandatoryItems = (rm: CompanyRmI, cv: StudentCvInterface, user: StudentInterface, games) => {
    if (!rm) return {
        done: 0,
        total: 0
    };

    const qualification = getDoneTotalNotOptionalQualifications(rm, cv);
    const job = getDoneTotalNotOptionalJobs(rm, cv);
    const language = getDoneTotalNotOptionalLanguages(rm, cv);
    const competence = getDoneTotalNotOptionalCompetences(rm, cv);
    const material = getDoneTotalNotOptionalMaterials(rm, cv);
    const game = getDoneTotalGames(rm, user, games);

    return {
        done: qualification.done + job.done + language.done + competence.done + material.done + game.done,
        total: qualification.total + job.total + language.total + competence.total + material.total + game.total
    };
}

export const getDoneTotalOptionalItems = (rm, cv) => {
    if (!rm) return {done: 0, total: 0};

    const qualification = getDoneTotalOptionalQualifications(rm, cv);
    const job = getDoneTotalOptionalJobs(rm, cv);
    const language = getDoneTotalOptionalLanguages(rm, cv);
    const competence = getDoneTotalOptionalCompetences(rm, cv);
    const material = getDoneTotalOptionalMaterials(rm, cv);

    return {
        done: qualification.done + job.done + language.done + competence.done + material.done,
        total: qualification.total + job.total + language.total + competence.total + material.total
    };
}

export const getDoneTotalNotOptionalCompetences = (rm: CompanyRmI, cv: StudentCvInterface) => {
    if (!rm) return {
        done: 0,
        total: 0
    };

    let done = 0;
    let total = 0;

    Object.values(rm.bg?.competences || {}).filter(x => !(x?.importance === 2)).forEach(c => {
        total++;
        if (hasCompetence(cv, c) === 2) done++;
    });

    return {done, total};
}

export const getDoneTotalOptionalCompetences = (rm: CompanyRmI, cv: StudentCvInterface) => {
    if (!rm) return {done: 0, total: 0};

    let done = 0;
    let total = 0;

    Object.values(rm.bg?.competences || {}).filter(x => (x?.importance === 2)).forEach(c => {
        total++;
        if (hasCompetence(cv, c) === 2) done++;
    });

    return {done, total};
}

export const getDoneTotalNotOptionalMaterials = (rm: CompanyRmI, cv: StudentCvInterface) => {
    if (!rm) return {
        done: 0,
        total: 0
    };

    let done = 0;
    let total = 0;

    Object.values(rm.bg?.materials || {}).forEach(m => {
        total++;
        if (hasMaterial(cv, m) === 1) done++;
    });

    return {done, total};
}

export const gamesWithTargetFromList = (games: CompanyGameInterface[], targets: string[]) => {
    return games.filter(game => {
        const target = game.target;
        if (!target) return true;
        else targets.includes(target);
    });
}

export const getDoneTotalGames = (rm, user, targetRmGames) => {
    if (!rm || !targetRmGames) return {done: 0, total: 0};

    let done = 0;
    let total = 0;

    targetRmGames.forEach(rmGame => {
        total++;

        const has = hasGame(user, rmGame);
        if (has === 1) done++;
    });

    return {done, total};
}

export const getDoneTotalOptionalMaterials = (rm, cv) => {
    if (!rm) return {done: 0, total: 0};

    let done = 0;
    let total = 0;

    // Object.values(rm.bg?.materials || {}).map(m => {
    // 	total++;
    // 	if (hasMaterial(cv, m) === 1) done++;
    // });

    return {done, total};
}

export const getDoneTotalNotOptionalTasks = (rm, cv) => {
    if (!rm || !cv) return {};

    let done = 0;
    let total = 0;

    // Object.values(rm.bg?.qualifications || {}).filter(x => !(x?.importance === 2)).map(q => {
    // 	total++;
    // 	if (hasQualification(cv, q) === 2) done++;
    // });
    const {done: doneQualifications, total: totalQualifications} = getDoneTotalNotOptionalQualifications(rm, cv);
    done += doneQualifications;
    total += totalQualifications;

    // Object.values(rm.bg?.jobs || {}).filter(x => !(x?.importance === 2)).map(j => {
    // 	total++;
    // 	if (hasJob(cv, j) === 2) done++;
    // });
    const {done: doneJobs, total: totalJobs} = getDoneTotalNotOptionalJobs(rm, cv);
    done += doneJobs;
    total += totalJobs;

    // Object.values(rm.bg?.languages || {}).filter(x => !(x?.importance === 2)).map(l => {
    // 	total++;
    // 	if (hasLanguage(cv, l) === 2) done++;
    // });
    const {done: doneLanguages, total: totalLanguages} = getDoneTotalNotOptionalLanguages(rm, cv);
    done += doneLanguages;
    total += totalLanguages;

    // Object.values(rm.bg?.competences || {}).filter(x => !(x?.importance === 2)).map(c => {
    // 	total++;
    // 	if (hasCompetence(cv, c) === 2) done++;
    // });
    const {done: doneCompetences, total: totalCompetences} = getDoneTotalNotOptionalCompetences(rm, cv);
    done += doneCompetences;
    total += totalCompetences;

    // Object.values(rm.bg?.materials || {}).map(m => {
    // 	total++;
    // 	if (hasMaterial(cv, m) === 1) done++;
    // });
    const {done: doneMaterials, total: totalMaterials} = getDoneTotalNotOptionalMaterials(rm, cv);
    done += doneMaterials;
    total += totalMaterials;

    return {done, total};
}

export const getDoneTotalOptionalTasks = (rm, cv) => {
    if (!rm || !cv) return {};

    let done = 0;
    let total = 0;

    // Object.values(rm.bg?.qualifications || {}).filter(x => x?.importance === 2).map(q => {
    // 	total++;
    // 	if (hasQualification(cv, q) === 2) done++;
    // });
    const {done: doneQualifications, total: totalQualifications} = getDoneTotalOptionalQualifications(rm, cv);
    done += doneQualifications;
    total += totalQualifications;

    // Object.values(rm.bg?.jobs || {}).filter(x => x?.importance === 2).map(j => {
    // 	total++;
    // 	if (hasJob(cv, j) === 2) done++;
    // });
    const {done: doneJobs, total: totalJobs} = getDoneTotalOptionalJobs(rm, cv);
    done += doneJobs;
    total += totalJobs;

    // Object.values(rm.bg?.languages || {}).filter(x => x?.importance === 2).map(l => {
    // 	total++;
    // 	if (hasLanguage(cv, l) === 2) done++;
    // });
    const {done: doneLanguages, total: totalLanguages} = getDoneTotalOptionalLanguages(rm, cv);
    done += doneLanguages;
    total += totalLanguages;

    // Object.values(rm.bg?.competences || {}).filter(x => x?.importance === 2).map(c => {
    // 	total++;
    // 	if (hasCompetence(cv, c) === 2) done++;
    // });
    const {done: doneCompetences, total: totalCompetences} = getDoneTotalOptionalCompetences(rm, cv);
    done += doneCompetences;
    total += totalCompetences;

    // Object.values(rm.bg?.materials || {}).map(m => {
    // 	total++;
    // 	if (hasMaterial(cv, m) === 1) done++;
    // });
    const {done: doneMaterials, total: totalMaterials} = getDoneTotalOptionalMaterials(rm, cv);
    done += doneMaterials;
    total += totalMaterials;

    return {done, total};
}

export const mandatoryItems = (items: RmBgItemWithImportanceI[] = []) => {
    return items.filter(x => !(x?.importance === 2));
}

export const optionalItems = (items: RmBgItemWithImportanceI[] = []) => {
    return items.filter(x => x?.importance === 2);
}

export const hasMaterial = (cv: StudentCvInterface, material: RmMaterialI) => {
    if ((cv?.bg?.materials || []).includes(material.id)) return 1;
    else return 0;
}

export const hasGame = (user: StudentInterface, game: CompanyGameInterface) => {
    if ((user?.games || []).map(game => game.id).includes(game.id)) return 1;
    else return 0;
}

export const sanitizeUrl = url => {
    if (!url) return "";
    const trimmedUrl = url.trim();
    const SAFE_URL_PATTERN = /^(?:(?:https?|mailto|ftp|tel|file|sms):|[^&:/?#]*(?:[/?#]|$))/gi;
    const DATA_URL_PATTERN = /^data:(?:image\/(?:bmp|gif|jpeg|jpg|png|tiff|webp)|video\/(?:mpeg|mp4|ogg|webm)|audio\/(?:mp3|oga|ogg|opus));base64,[a-z0-9+/]+=*$/i;

    if (trimmedUrl == null || trimmedUrl.length === 0) return "";
    if (trimmedUrl.match(SAFE_URL_PATTERN) || trimmedUrl.match(DATA_URL_PATTERN)) return trimmedUrl;

    return "";
}

export const calculateSoftSkills = (answers, answerChoiceNumber = 10) => {
    // answers = [{ qId, value, tag }, {...}]
    if (!answers) return null;

    let softSkills = {};
    Object.keys(softSkillsDataset).forEach(k => softSkills[k] = 0);

    answers.forEach(a => softSkills[a.tag] += a.value);

    Object.entries(softSkills).forEach(e => {
        const tag = e[0];
        const divider = answers.filter(a => a.tag === tag).length;

        softSkills[tag] = softSkills[tag] / divider;
        softSkills[tag] = softSkills[tag] * 10 / answerChoiceNumber;
    })
    return softSkills;
}

export const isExpired = deadline => {
    if (!deadline) return false;

    const now = Date.now();
    return deadline.seconds * 1000 < now;
}

export const isFollowingGameExpired = (game, followingAt) => {
    if (!game) return true;

    const deadline = game?.deadline;
    const durationDays = game?.durationDays;
    const now = Date.now();

    if (deadline) {
        return deadline.seconds * 1000 < now;
    } else if (durationDays) {
        if (!followingAt)
            return true;
        else {
            const followMillisec = followingAt.seconds * 1000;
            const offsetMillisec = durationDays * 24 * 60 * 60 * 1000; // milliseconds in 'durationDays' days
            return followMillisec + offsetMillisec < now;
        }
    } else {
        return false;
    }
}

export const computeGameDeadline = (game, followingTime) => {
    if (!game) return null;

    const deadline = game?.deadline;
    const durationDays = game?.durationDays;

    if (deadline) {
        return moment(deadline.seconds * 1000).format("YYYY-MM-DD");
    } else if (durationDays) {
        if (!followingTime)
            return null;
        else {
            const followMillisec = followingTime.seconds * 1000;
            const offsetMillisec = durationDays * 24 * 60 * 60 * 1000; // milliseconds in 'durationDays' days
            return moment(followMillisec + offsetMillisec).format("DD/MM/YY");
        }
    } else {
        return null;
    }
}

export const createId = () => {
    return Math.random().toString(36).substr(2, 9);
}

export const downloadFileFromUrl = url => {
    return new Promise(function (resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open("GET", url, true);
        xhr.responseType = "arraybuffer";
        xhr.onreadystatechange = function (evt) {
            if (xhr.readyState === 4) {
                if (xhr.status === 200) {
                    resolve(xhr.response);
                } else {
                    reject("Error");
                }
            }
        };
        xhr.send();
    });
}

export const capitalizeString = (name: string, delimiter: string = ' ') => {
    if(!name) return "";
    return name.split(delimiter).map(
        (str) => str.charAt(0).toUpperCase() + str.slice(1)).join(' ')
}

export function isEmpty(obj: object) {
    return Object.keys(obj).length === 0;
}